import {
  navigateTo,
  useRoute,
  useRouteLocationWithLocale,
  useRuntimeConfig,
} from '#imports'

import { useIntercom } from '@backmarket/nuxt-layer-care-platform/intercom/useIntercom'
import { toNuxtLink } from '@backmarket/nuxt-module-navigation/toNuxtLink'
import { useTracking } from '@backmarket/nuxt-module-tracking/useTracking'
import { isEmpty } from '@backmarket/utils/object/isEmpty'
import { openModal } from '@ds/components/ModalBase'

import { useIntercomGuestQuery } from '@/scopes/care-commons/composables/useIntercomGuestQuery'
import { SALES_CUSTOMER_CARE_SCOPE } from '@/scopes/care-commons/utils/logger/salesCustomerCare/config'
import { useSalesCustomerCareLogger } from '@/scopes/care-commons/utils/logger/salesCustomerCare/useSalesCustomerCareLogger'
import { CUSTOMER_CARE } from '@/scopes/customer-care/route-names'
import type { Orderline } from '~/api/my-orders/types/OrderlineList.types'
import { CUSTOMER_CONVERSATION } from '~/scopes/customer-conversation/route-names'
import {
  MY_ORDERS_ZONE,
  PARCEL_JOURNEY_TYPE_FORWARD,
  TRACK_PARCEL_CTA_TRACK_CLICK_NAME,
  TRACK_PARCEL_TRACK_CLICK_TARGET_CARRIER,
  TRACK_PARCEL_TRACK_CLICK_TARGET_INTERNAL,
} from '~/scopes/customer-delivery/pages/TrackParcel/TrackParcel.constants'
import { ROUTE_NAMES as DASHBOARD_ROUTE_NAMES } from '~/scopes/dashboard/constants'

import { ORDERLINE_CARD_MODAL } from '../OrderlineCard.constants'

export type OrderlineCardActionsConfig = {
  config: {
    [key in Orderline['actions'][number]['type']]: (
      item: Orderline['actions'][number],
    ) => {
      onClick?: (orderline: Orderline) => Promise<void>
      loading?: boolean
    }
  }
}

export const useOrderlineCardActionsConfig = (): OrderlineCardActionsConfig => {
  const { status, intercom, load } = useIntercom()
  const currentRoute = useRoute()
  const getIntercomGuestQuery = useIntercomGuestQuery()

  const injectLocale = useRouteLocationWithLocale()
  const { logFeatureError } = useSalesCustomerCareLogger()
  const { FF_ENABLE_MY_ORDERS_TRACK_PARCEL } = useRuntimeConfig().public
  const { trackClick } = useTracking()

  const defineRouterLink = ({
    name,
    params,
    query,
  }: {
    name: string
    params: Record<string, string | number>
    query?: Record<string, string>
  }) => {
    return toNuxtLink(
      injectLocale({
        type: 'internal',
        name,
        params,
        query,
      }),
    )
  }

  return {
    config: {
      cancel_order_before_validation: () => ({
        onClick: async () => {
          /*
           * https://backmarket.atlassian.net/browse/SCAN-5870
           */
          logFeatureError({
            errorDetail:
              "The 'cancel order before validation' action is disabled and cannot be clicked.",
            errorTitle: `${SALES_CUSTOMER_CARE_SCOPE.myOrders} Error on my orders cta config`,
            featureName: 'my_oders_cta_config',
          })
        },
      }),
      cancel_order_pending_payment: () => ({
        onClick: async ({ orderlineId }) => {
          openModal(
            `${ORDERLINE_CARD_MODAL.cancelOrderWithPendingPayment}-${orderlineId}`,
          )
        },
      }),
      diagnosis: (item) => ({
        onClick: async () => {
          if (item?.link && 'params' in item.link) {
            const { orderlineId } = item.link.params

            await navigateTo(
              defineRouterLink({
                name: CUSTOMER_CARE.DIAGNOSIS.SELF,
                params: { orderlineId },
              }),
            )
          }
        },
      }),
      diagnosis_new_issue: (item) => ({
        onClick: async () => {
          if (item?.link && 'params' in item.link) {
            const { orderlineId } = item.link.params

            await navigateTo(
              defineRouterLink({
                name: CUSTOMER_CARE.DIAGNOSIS.SELF,
                params: { orderlineId },
              }),
            )
          }
        },
      }),
      join_conversation: (item) => ({
        onClick: async () => {
          if (item?.link && 'params' in item.link) {
            const { resolutionId } = item.link.params

            const route = CUSTOMER_CONVERSATION.RESOLUTION

            await navigateTo(
              defineRouterLink({
                name: route,
                params: { resolutionId },
              }),
            )
          }
        },
      }),
      live_chat: (item) => ({
        onClick: async () => {
          if (item?.link && 'params' in item.link) {
            const { resolutionId } = item.link.params

            await navigateTo(
              defineRouterLink({
                name: CUSTOMER_CONVERSATION.RESOLUTION,
                params: { resolutionId },
              }),
            )
          }
        },
      }),
      open_conversation: (item) => ({
        onClick: async ({ orderlineId }) => {
          if (item?.link && 'params' in item.link) {
            const { conversationId } = item.link.params

            if (conversationId) {
              if (status.value !== 'loaded') {
                await load()
              }

              if (status.value === 'loaded') {
                intercom('showConversation', conversationId)

                await navigateTo({
                  query: {
                    ...currentRoute.query,
                    intercom_ticket_id: conversationId,
                    ...getIntercomGuestQuery(currentRoute.query),
                  },
                  replace: true,
                })
              }

              return
            }
          }

          logFeatureError({
            errorTitle: `${SALES_CUSTOMER_CARE_SCOPE.resolutionFlow} [Intercom] - Error trying to open showConversation without tiket id`,
            errorDetail: {
              orderlineId,
              actionLink: item?.link,
            },
            featureName: 'resolution_flow_show_intercom_conversation',
          })
        },
        loading:
          status.value === 'awaitingLoad' ? false : status.value !== 'loaded',
      }),
      order_bill: (item) => ({
        onClick: async () => {
          if (!isEmpty(item.link)) {
            window.open(item.link.href, '_blank', 'noopener,noreferrer')
          }
        },
      }),
      pending_order_bill: () => ({}),
      sale_certificate: (item) => ({
        onClick: async () => {
          if (!isEmpty(item.link)) {
            window.open(item.link.href, '_blank', 'noopener,noreferrer')
          }
        },
      }),
      see_return_confirmation: (item) => ({
        onClick: async () => {
          if (item?.link && 'params' in item.link) {
            const { resolutionId } = item.link.params

            await navigateTo(
              defineRouterLink({
                name: CUSTOMER_CARE.RESOLUTION.RESOLUTION_RETURN_CONFIRMATION,
                params: { resolutionId },
              }),
            )
          }
        },
      }),
      see_express_replacement_confirmation: (item) => ({
        onClick: async () => {
          if (item?.link && 'params' in item.link) {
            const { resolutionId } = item.link.params

            await navigateTo(
              defineRouterLink({
                name: CUSTOMER_CARE.RESOLUTION
                  .RESOLUTION_EXPRESS_REPLACEMENT_CONFIRMATION,
                params: { resolutionId },
              }),
            )
          }
        },
      }),
      track_parcel: (item) => ({
        onClick: async ({ orderId, publicId, state }) => {
          const isMyOrdersTrackParcelPageEnabled =
            FF_ENABLE_MY_ORDERS_TRACK_PARCEL

          trackClick({
            name: TRACK_PARCEL_CTA_TRACK_CLICK_NAME,
            zone: MY_ORDERS_ZONE,
            values: {
              journey: PARCEL_JOURNEY_TYPE_FORWARD,
              orderId,
              orderlinePublicId: publicId,
              orderlineState: state.enumState,
              target: isMyOrdersTrackParcelPageEnabled
                ? TRACK_PARCEL_TRACK_CLICK_TARGET_INTERNAL
                : TRACK_PARCEL_TRACK_CLICK_TARGET_CARRIER,
            },
          })

          if (isMyOrdersTrackParcelPageEnabled) {
            await navigateTo(
              defineRouterLink({
                name: DASHBOARD_ROUTE_NAMES.DASHBOARD.MY_ORDERS.TRACK_PARCEL,
                params: { orderlinePublicId: publicId },
              }),
            )
          } else if (!isEmpty(item.link)) {
            window.open(item.link.href, '_blank', 'noopener,noreferrer')
          }
        },
      }),
      select_your_orderline: (item) => ({
        onClick: async () => {
          if (item.link?.type === 'internal') {
            const { hash: _hash, ...link } = item.link
            await navigateTo(defineRouterLink(link))
          }
        },
      }),
    },
  }
}
